import { lineItemDefaultData } from '../store/dataSlice'

/**
 * Transform form data to Xero API format
 * @param {Object} formData - Form data from Formik
 * @param {Array} customers - Available customers list
 * @returns {Object} Xero-formatted invoice data
 */
export const transformFormDataToXeroFormat = (formData, customers) => {
    // Find the selected customer
    const selectedCustomer = customers.find(c => c.value === formData.customer)

    if (!selectedCustomer) {
        throw new Error('Please select a valid customer')
    }

    // Map line items to Xero format with proper field names
    const lineItems = formData.line_items.map(item => ({
        Description: item.description,
        Quantity: parseFloat(item.quantity),
        UnitAmount: parseFloat(item.unit_amount),
        AccountCode: item.account_code,
        TaxType: item.tax_type,
    }))

    const invoiceData = {
        Type: 'ACCREC',
        Contact: {
            Name: selectedCustomer.label,
        },
        Date: formData.invoice_date,
        LineItems: lineItems,
        Status: 'DRAFT',
        Reference: formData.reference || '',
    }

    // Only include DueDate if it's provided, otherwise let Xero use customer default
    if (formData.due_date) {
        invoiceData.DueDate = formData.due_date
    }

    return invoiceData
}

/**
 * Get initial form values for create/edit mode
 * @param {string} id - Invoice ID (for edit mode)
 * @param {Object} recordData - Record data from API
 * @param {Array} customers - Available customers
 * @param {Array} accountCodes - Available account codes
 * @returns {Object} Initial form values
 */
export const getInitialValues = (id, recordData, customers, accountCodes) => {
    if (id && recordData) {
        // Transform backend data structure to match form structure
        console.log('Edit mode - transforming recordData:', recordData)
        console.log('Current customers:', customers)
        console.log('Current account codes:', accountCodes)

        // Map line items from backend format to form format
        const transformedLineItems =
            recordData.line_items?.map(item => {
                // Use account_code_code (the actual code string) instead of account_code (UUID)
                // The backend serializer provides both: account_code (UUID) and account_code_code (string)
                let accountCode = item.account_code_code || '200'

                // Only validate account code if we have account codes loaded AND it's not empty
                // This prevents premature replacement when account codes are still loading
                if (accountCodes && accountCodes.length > 0 && accountCode) {
                    const validAccountCode = accountCodes.find(ac => ac.value === accountCode)
                    if (!validAccountCode) {
                        // Log the issue but preserve the original account code
                        // The Select component will handle invalid values gracefully
                        console.warn(
                            `Account code ${accountCode} not found in current dropdown options. This may be due to data loading timing or the account code being from a different configuration.`
                        )
                        // Keep the original account code - don't replace it
                        // accountCode = accountCodes[0].value
                    }
                }

                console.log(
                    `Line item account code: ${accountCode}, original UUID: ${item.account_code}, code: ${item.account_code_code}`
                )

                return {
                    description: item.description || '',
                    quantity: item.quantity || 1,
                    unit_amount: item.unit_amount || 0,
                    account_code: accountCode,
                    tax_type: item.tax_type || 'OUTPUT',
                    line_total: parseFloat(item.quantity || 0) * parseFloat(item.unit_amount || 0) || 0,
                }
            }) || []

        // If no line items, add a default one
        if (transformedLineItems.length === 0) {
            transformedLineItems.push({ ...lineItemDefaultData })
        }

        // Debug the contact structure
        console.log('Contact data structure:', recordData.contact)
        console.log('Contact ID:', recordData.contact_id)
        console.log('Contact name:', recordData.contact_name)

        // Use the contact_id field directly from the serializer
        let customerId = recordData.contact_id

        console.log('Using contact_id from serializer:', customerId)
        console.log(
            'Available customer IDs:',
            customers?.map(c => c.value)
        )

        // Check if the customerId exists in the customers list
        const customerExists = customers && customers.length > 0 && customers.some(c => c.value === customerId)

        console.log('Customer exists in dropdown:', customerExists)

        // If customer doesn't exist in dropdown options, preserve the original customerId
        if (!customerExists && customers && customers.length > 0) {
            console.log('Customer not found in dropdown, preserving original customerId')
            // Keep the original customerId - this allows the form to show the correct customer
            // even if it's not currently in the dropdown options
        }

        return {
            // Map customer ID from contact - handle different possible formats
            customer: customerId, // Changed from contact_id to customer to match form field name
            // Format dates properly
            invoice_date: recordData.date || new Date().toISOString().split('T')[0],
            due_date: recordData.due_date || '',
            reference: recordData.reference || '',
            line_items: transformedLineItems,
            files: [],
        }
    }
    return {
        customer: '',
        invoice_date: new Date().toISOString().split('T')[0],
        due_date: '',
        reference: '',
        line_items: [{ ...lineItemDefaultData }],
        files: [],
    }
}
